// Automacoes.tsx
//
// Sub-tela "Automações" de Comunicações: a regra que amarra os três pedaços que
// já existem em telas diferentes — EVENTO (o gatilho, que continua em
// Suprimentos) → CANAL (aba Canais) → TEMPLATE (aba Templates) → PÚBLICO (quem
// recebe).
//
// Onde isso vai: acd."ActOrganizationInfo", organizationInfoType 21. Mesma
// armadilha dos outros agregados: o tipo 1 é SKU e o 20 é template; reusar um
// número já ocupado grava um agregado por cima do outro sem erro nenhum.
//
// Esta tela NÃO dispara nem testa nada. Criar a regra é dizer o que aconteceria
// quando o alerta de Suprimentos disparar — não é mandar mensagem.

import React, { useEffect, useRef, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/comunicacoesSection/index";
import {
  TEMPLATES_PADRAO,
  lerTemplatesGravados,
} from "../suppliesSection/templatesPadrao";

/** organizationInfoType das automações. Ver a armadilha no cabeçalho. */
const ORGANIZATION_INFO_AUTOMACAO = 21;

/**
 * Os eventos são os gatilhos que EXISTEM hoje, os três de Suprimentos → Alertas
 * e Notificações. A lista não inventa "pedido saiu para entrega" nem "pedido
 * entregue": nada no sistema levanta esses eventos, e oferecê-los criaria
 * regra que nunca vai rodar. Evento novo aqui exige gatilho novo lá.
 */
const EVENTOS = [
  {
    chave: "estoqueBaixo",
    rotulo: "Estoque abaixo do ponto de reposição",
  },
  {
    chave: "pedidoAutomatico",
    rotulo: "Pedido automático gerado para o fornecedor",
  },
  {
    chave: "validadeProxima",
    rotulo: "Produto próximo do vencimento",
  },
];

const CANAIS = [
  { chave: "whatsapp", rotulo: "WhatsApp" },
  { chave: "email", rotulo: "E-mail" },
  { chave: "sms", rotulo: "SMS" },
];

/**
 * Os três públicos que os eventos acima alcançam. "Cliente" fica de fora de
 * propósito: nenhum gatilho de Suprimentos fala com o cliente final — estoque,
 * reposição e validade são assunto de dentro da operação.
 */
const PUBLICOS = [
  { chave: "donoDaRede", rotulo: "Quem administra a rede" },
  { chave: "equipeDaLoja", rotulo: "Equipe da loja" },
  { chave: "fornecedor", rotulo: "Fornecedor do item" },
];

interface Formulario {
  evento: string;
  canal: string;
  template: string;
  publico: string;
  ativa: boolean;
}

type Aviso = { tom: "ok" | "erro" | "atencao"; texto: string };

function formularioVazio(): Formulario {
  return { evento: "", canal: "", template: "", publico: "", ativa: true };
}

function rotuloDe(
  lista: { chave: string; rotulo: string }[],
  chave: string
): string {
  const achado = lista.find((item) => item.chave === chave);
  // Uma regra gravada com chave que a tela não conhece mais tem que aparecer
  // como está, e não sumir: sumir esconderia uma automação em vigor.
  return achado ? achado.rotulo : chave || "(não definido)";
}

interface Props {
  decryptedInfoToken: any;
  /** A implementação real devolve Promise de { ok, tom, texto } — ver configuration/index.tsx. */
  handleSave: (objectType: any, payload?: any) => any;
}

const Automacoes: React.FC<Props> = ({ decryptedInfoToken, handleSave }) => {
  const [form, setForm] = useState<Formulario>(formularioVazio());
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Guarda de unmount: `handleSave` é assíncrono e o usuário pode trocar de aba
  // no meio; sem isto o setState de depois do await cai num componente morto.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const informacoes: any[] =
    decryptedInfoToken?.organization?.organizationInfo ?? [];

  // O seletor grava o NOME do template, não o uuid: modelo do sistema ainda não
  // tem uuid, e uma regra que só pudesse apontar para template já gravado seria
  // inútil enquanto nenhum template pode ser gravado. A origem vai escrita no
  // rótulo — escolher um modelo que não está no banco é decisão do usuário, não
  // surpresa. A leitura do gravado é a mesma de Templates (templatesPadrao.ts).
  const opcoesDeTemplate: { chave: string; rotulo: string }[] = [
    ...lerTemplatesGravados(decryptedInfoToken).map((template) => ({
      chave: template.nome,
      rotulo: `${template.nome} (gravado)`,
    })),
    ...TEMPLATES_PADRAO.map((semente) => ({
      chave: semente.nome,
      rotulo: `${semente.nome} (modelo do sistema)`,
    })),
  ];

  const automacoes = informacoes
    .filter(
      (info: any) =>
        Number(info?.organizationInfoType) === ORGANIZATION_INFO_AUTOMACAO
    )
    .map((info: any) => {
      // Valor malformado não derruba a lista: a linha aparece com os campos em
      // branco, e continua visível.
      let valor: any = {};
      try {
        valor = JSON.parse(info?.organizationInfoValue || "{}");
      } catch {
        valor = {};
      }
      return {
        uuid: info?.organizationInfoUuid,
        nome: info?.organizationInfoName,
        evento: String(valor?.evento ?? ""),
        canal: String(valor?.canal ?? ""),
        template: String(valor?.template ?? ""),
        publico: String(valor?.publico ?? ""),
        ativa: valor?.ativa !== false,
      };
    });

  const mudar = (campo: keyof Formulario, valor: string | boolean) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
  };

  const adicionar = async () => {
    // Validação antes de qualquer chamada: campo em branco é problema da tela.
    if (!form.evento) {
      setAviso({ tom: "erro", texto: "Escolha o evento que dispara a regra." });
      return;
    }
    if (!form.canal) {
      setAviso({ tom: "erro", texto: "Escolha por qual canal a mensagem sai." });
      return;
    }
    if (!form.template) {
      setAviso({
        tom: "erro",
        texto:
          opcoesDeTemplate.length === 0
            ? "Não há nenhum template para escolher. Crie um em Comunicações → Templates."
            : "Escolha o template com o texto da mensagem.",
      });
      return;
    }
    if (!form.publico) {
      setAviso({ tom: "erro", texto: "Escolha quem recebe a mensagem." });
      return;
    }

    setSalvando(true);
    const resultado = await handleSave("automacao", {
      organizationInfoType: ORGANIZATION_INFO_AUTOMACAO,
      valor: {
        versao: 1,
        evento: form.evento,
        canal: form.canal,
        template: form.template,
        publico: form.publico,
        ativa: form.ativa,
      },
    });

    if (!montado.current) return;
    setSalvando(false);
    setAviso(
      resultado
        ? { tom: resultado.tom, texto: resultado.texto }
        : {
            tom: "atencao",
            texto:
              "O salvamento não respondeu nada. Nada foi confirmado — confira " +
              "antes de considerar gravado.",
          }
    );
    // O formulário só é limpo quando a gravação deu certo: limpar depois de uma
    // recusa apagaria o que o usuário digitou junto com a chance de reenviar.
    if (resultado?.ok) setForm(formularioVazio());
  };

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.Header>
        <AlfredoStyles.HeaderContent>
          <AlfredoStyles.HeaderTitle>
            <AlfredoStyles.MaterialIcons
              style={{ fontSize: "calc(var(--u) * 36)" }}
            >
              rule
            </AlfredoStyles.MaterialIcons>
            Automações
          </AlfredoStyles.HeaderTitle>
          <AlfredoStyles.HeaderSubtitle>
            Evento → canal → template → público. A regra diz o que aconteceria
            quando o alerta de Suprimentos disparar; nada é enviado a partir
            daqui.
          </AlfredoStyles.HeaderSubtitle>
        </AlfredoStyles.HeaderContent>
      </AlfredoStyles.Header>

      {/* Antes da digitação, não depois do clique. */}
      <AlfredoStyles.AvisoTopo $tom="atencao">
        Gravar automação ainda não está ligado. O botão chama
        <code> automacao</code>, string que o salvamento do shell não conhece:
        ele devolve a recusa nomeada e nenhuma requisição sai. A regra montada
        aqui serve para conferir o desenho — ela não passa a valer.
      </AlfredoStyles.AvisoTopo>

      {/* ---------------- FORMULÁRIO INLINE ---------------- */}
      <AlfredoStyles.Cartao>
        <AlfredoStyles.CartaoTitulo>
          <AlfredoStyles.MaterialIcons>add_task</AlfredoStyles.MaterialIcons>
          Nova automação
        </AlfredoStyles.CartaoTitulo>
        <AlfredoStyles.CartaoSubtitulo>
          Os eventos da lista são os gatilhos que existem hoje, em Suprimentos →
          Alertas e Notificações.
        </AlfredoStyles.CartaoSubtitulo>

        <AlfredoStyles.CampoGrid>
          <AlfredoStyles.Campo>
            <AlfredoStyles.RotuloCampo>Evento</AlfredoStyles.RotuloCampo>
            <AlfredoStyles.Seletor
              value={form.evento}
              onChange={(e) => mudar("evento", e.target.value)}
            >
              <option value="">Escolha o evento…</option>
              {EVENTOS.map((evento) => (
                <option key={evento.chave} value={evento.chave}>
                  {evento.rotulo}
                </option>
              ))}
            </AlfredoStyles.Seletor>
          </AlfredoStyles.Campo>

          <AlfredoStyles.Campo>
            <AlfredoStyles.RotuloCampo>Canal</AlfredoStyles.RotuloCampo>
            <AlfredoStyles.Seletor
              value={form.canal}
              onChange={(e) => mudar("canal", e.target.value)}
            >
              <option value="">Escolha o canal…</option>
              {CANAIS.map((canal) => (
                <option key={canal.chave} value={canal.chave}>
                  {canal.rotulo}
                </option>
              ))}
            </AlfredoStyles.Seletor>
            <AlfredoStyles.AjudaCampo>
              O remetente e a credencial do canal são configurados por loja, em
              Comunicações → Canais.
            </AlfredoStyles.AjudaCampo>
          </AlfredoStyles.Campo>

          <AlfredoStyles.Campo>
            <AlfredoStyles.RotuloCampo>Template</AlfredoStyles.RotuloCampo>
            <AlfredoStyles.Seletor
              value={form.template}
              onChange={(e) => mudar("template", e.target.value)}
            >
              <option value="">
                {opcoesDeTemplate.length === 0
                  ? "Nenhum template disponível"
                  : "Escolha o template…"}
              </option>
              {/* A chave leva o índice porque o rótulo NÃO é único: nada impede
                  a rede de gravar dois templates com o mesmo nome, e aí duas
                  <option> sairiam com a mesma key. */}
              {opcoesDeTemplate.map((opcao, indice) => (
                <option key={`${indice}-${opcao.rotulo}`} value={opcao.chave}>
                  {opcao.rotulo}
                </option>
              ))}
            </AlfredoStyles.Seletor>
          </AlfredoStyles.Campo>

          <AlfredoStyles.Campo>
            <AlfredoStyles.RotuloCampo>Público</AlfredoStyles.RotuloCampo>
            <AlfredoStyles.Seletor
              value={form.publico}
              onChange={(e) => mudar("publico", e.target.value)}
            >
              <option value="">Escolha quem recebe…</option>
              {PUBLICOS.map((publico) => (
                <option key={publico.chave} value={publico.chave}>
                  {publico.rotulo}
                </option>
              ))}
            </AlfredoStyles.Seletor>
          </AlfredoStyles.Campo>
        </AlfredoStyles.CampoGrid>

        <AlfredoStyles.Linha>
          <AlfredoStyles.Caixa
            type="checkbox"
            checked={form.ativa}
            onChange={(e) => mudar("ativa", e.target.checked)}
          />
          Regra ativa
        </AlfredoStyles.Linha>

        <AlfredoStyles.AcaoRodape>
          <AlfredoStyles.Button disabled={salvando} onClick={adicionar}>
            <AlfredoStyles.MaterialIcons style={{ fontSize: "var(--fs-base)" }}>
              save
            </AlfredoStyles.MaterialIcons>
            {salvando ? "Salvando…" : "Adicionar automação"}
          </AlfredoStyles.Button>
        </AlfredoStyles.AcaoRodape>

        {aviso && (
          <AlfredoStyles.Aviso $tom={aviso.tom}>
            {aviso.texto}
          </AlfredoStyles.Aviso>
        )}
      </AlfredoStyles.Cartao>

      {/* ---------------- O QUE ESTÁ GRAVADO ---------------- */}
      {automacoes.length === 0 ? (
        <AlfredoStyles.EmptyState>
          <AlfredoStyles.EmptyStateIcon>🔗</AlfredoStyles.EmptyStateIcon>
          <AlfredoStyles.EmptyStateTitle>
            Nenhuma automação gravada
          </AlfredoStyles.EmptyStateTitle>
          <AlfredoStyles.EmptyStateDescription>
            A rede não tem nenhuma linha de automação no infoToken. Nada está
            oculto aqui: a lista mostra exatamente o que está gravado.
          </AlfredoStyles.EmptyStateDescription>
        </AlfredoStyles.EmptyState>
      ) : (
        <AlfredoStyles.Lista>
          {automacoes.map((automacao: any) => (
            <AlfredoStyles.ListaItem key={automacao.uuid}>
              <AlfredoStyles.ListaItemCorpo>
                <AlfredoStyles.ListaItemTitulo>
                  {rotuloDe(EVENTOS, automacao.evento)}
                  <AlfredoStyles.Etiqueta
                    $tom={automacao.ativa ? "gravado" : "neutro"}
                  >
                    {automacao.ativa ? "ativa" : "inativa"}
                  </AlfredoStyles.Etiqueta>
                </AlfredoStyles.ListaItemTitulo>
                <AlfredoStyles.ListaItemTexto>
                  {rotuloDe(CANAIS, automacao.canal)} · template “
                  {automacao.template || "(não definido)"}” ·{" "}
                  {rotuloDe(PUBLICOS, automacao.publico)}
                </AlfredoStyles.ListaItemTexto>
              </AlfredoStyles.ListaItemCorpo>
            </AlfredoStyles.ListaItem>
          ))}
        </AlfredoStyles.Lista>
      )}
    </AlfredoStyles.FieldsContainer>
  );
};

export default Automacoes;
